import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import PerformanceMonitor from "./components/PerformanceMonitor";
import SEO from './components/SEO';
import { Home } from './pages/Home';
import { Services } from './pages/Services';
import { Inquiry } from './pages/Inquiry';
import { About } from './pages/About';
import { CustomCakes } from './pages/CustomCakes';
import { Toaster } from "./components/ui/sonner";
import { preloadCriticalResources, trackPageView } from './utils/seoUtils';

// Lazy load less frequently accessed pages
const Blog = lazy(() => import('./pages/Blog'));
const MemberPolicy = lazy(() => import('./pages/MemberPolicy'));
const Careers = lazy(() => import('./pages/Careers'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const Investors = lazy(() => import('./pages/Investors'));
const Contributors = lazy(() => import('./pages/Contributors'));
const Feedback = lazy(() => import('./pages/Feedback'));
const FAQs = lazy(() => import('./pages/FAQs'));
const Charity = lazy(() => import('./pages/Charity'));
const FarmToTable = lazy(() => import('./pages/FarmToTable'));
const GiftPackages = lazy(() => import('./pages/GiftPackages'));
const Corporate = lazy(() => import('./pages/Corporate'));
const Testimony = lazy(() => import('./pages/Testimony'));
const TestimonialSubmission = lazy(() => import('./pages/TestimonialSubmission'));
const TravelWithChef = lazy(() => import('./pages/TravelWithChef'));
const WellnessRetreat = lazy(() => import('./pages/WellnessRetreat'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const LoyaltyDashboard = lazy(() => import('./pages/LoyaltyDashboardEnhanced'));
// Removed IndianFusionMenu - now integrated into Services page
const BookingCalendar = lazy(() => import('./pages/BookingCalendar'));
const ArtGallery = lazy(() => import('./pages/ArtGallery'));
const Giveaway = lazy(() => import('./pages/Giveaway'));
const VirtualConsultations = lazy(() => import('./pages/VirtualConsultations'));
const MealSubscriptions = lazy(() => import('./pages/MealSubscriptions'));
const ChefMarketplace = lazy(() => import('./pages/ChefMarketplace'));
const EventPlanning = lazy(() => import('./pages/EventPlanning'));
import CustomerPortal from './pages/CustomerPortal';
const ChefPortal = lazy(() => import('./pages/ChefPortal'));
const SupperClub = lazy(() => import('./pages/SupperClub'));

function App() {
  useEffect(() => {
    // Preload critical resources for performance
    preloadCriticalResources();
    
    // Track initial page load
    trackPageView('App Loaded');
    
    // Add performance observer for Core Web Vitals
    if ('web-vitals' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <SEO />
          <PerformanceMonitor />
          <BrowserRouter>
        <Navigation />
        <main>
          <Suspense fallback={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '50vh',
              fontSize: '18px',
              color: 'var(--color-gold)'
            }}>
              Loading...
            </div>
          }>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route path="/inquiry" element={<Inquiry />} />
              <Route path="/about" element={<About />} />
              <Route path="/custom-cakes" element={<CustomCakes />} />
              <Route path="/member-policy" element={<MemberPolicy />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/contributors" element={<Contributors />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/charity" element={<Charity />} />
              <Route path="/farm-to-table" element={<FarmToTable />} />
              <Route path="/gift-packages" element={<GiftPackages />} />
              <Route path="/corporate" element={<Corporate />} />
              <Route path="/testimony" element={<Testimony />} />
              <Route path="/testimonial-submission" element={<TestimonialSubmission />} />
              <Route path="/travel-with-chef" element={<TravelWithChef />} />
              <Route path="/wellness-retreat" element={<WellnessRetreat />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/loyalty" element={<LoyaltyDashboard />} />
              <Route path="/booking" element={<BookingCalendar />} />
              <Route path="/booking-calendar" element={<BookingCalendar />} />
              <Route path="/art-gallery" element={<ArtGallery />} />
              <Route path="/giveaway" element={<Giveaway />} />
              <Route path="/consultations" element={<VirtualConsultations />} />
              <Route path="/meal-plans" element={<MealSubscriptions />} />
              <Route path="/chef-marketplace" element={<ChefMarketplace />} />
              <Route path="/event-planning" element={<EventPlanning />} />
              <Route path="/customer-portal" element={<CustomerPortal />} />
              <Route path="/chef-portal" element={<ChefPortal />} />
              <Route path="/supper-club" element={<SupperClub />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <Toaster />
      </BrowserRouter>
      </div>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
